import { languages, useTranslations, useTranslationsDispatch } from "components/Translations/TranslationsProvider";
import { useCallback, useEffect } from 'react';
import { LanguagesEnum } from "services/tenantManagementService";
import { envSettings } from "settings/envSettings";
import { replacePlaceholders } from 'utils/stringUtil';

const LOCALE = 'locale';

export const setLocale = (locale: LanguagesEnum) => {
	localStorage.setItem(LOCALE, locale);
}

export const getLocale = (): LanguagesEnum => {
	const locale = localStorage.getItem(LOCALE);
	if (locale) {
		return locale as LanguagesEnum;
	}

	return LanguagesEnum.English;
}

export const getLangCode = () => {
	const locale = getLocale();
	return languages[locale].locale;
}

export const getLanguageFromLocale = (locale: LanguagesEnum | undefined) => {
	switch (locale) {
		case LanguagesEnum.Serbian:
			return languages[LanguagesEnum.Serbian];
		case LanguagesEnum.English:
		default:
			return languages[LanguagesEnum.English];
	}
}

export const useLoadTranslation = () => {
	const dispatch = useTranslationsDispatch();
	const { loadedTranslations, loadingInProgress } = useTranslations();

	return useCallback(
		async (routeId: string) => {
			if (loadedTranslations[routeId] || loadingInProgress.has(routeId)) {
				return;
			}

			dispatch({ type: 'LOAD_TRANSLATION', payload: routeId });
			const locale = getLocale();
			setLocale(locale);
			const languageCode = languages[locale].locale;
			try {
				const apiUrl = envSettings.apiUrl;
				const translationsText = await (await fetch(`${apiUrl}/translations/${languageCode}/${routeId}.json`)).text();
				const translations = JSON.parse(translationsText);
				dispatch({ type: 'SET_TRANSLATION_LOADED', payload: { routeId, translations } });
			} catch (error) {
				console.error(`Error loading translation for ${routeId}:`, error);
			}
		},
		[dispatch, loadedTranslations, loadingInProgress]
	)
}

export const useLoadTranslationEffect = (routeId: string) => {
	const loadedTranslation = useLoadTranslation();
	useEffect(
		() => {
			loadedTranslation(routeId);
		},
		[routeId, loadedTranslation]
	)
}

export const isNotTranslated = (value: string) => {
	return value.startsWith('i18n');
}

export const useGetTranslation = () => {
	const { translations } = useTranslations();
	return useCallback(
		(i18n: string | undefined, args?: string[]) => {
			if (i18n === undefined) {
				return ''
			}

			const translation = translations[i18n];

			let translatedArgs: string[] = [];
			if (args) {
				for (let arg of args) {
					let translatedArg = arg;
					if (arg && arg.startsWith('i18n')) {
						translatedArg = translations[arg] || arg; // if translation for that key doesn't exist, use arg
					}
					translatedArgs.push(translatedArg);
				}
			}

			if (translation) {
				const translatedText = replacePlaceholders(translation, translatedArgs);
				return translatedText;
			}

			return i18n;
		},
		[translations]
	)
}
